import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
	CircularProgress,
	Chip,
	Typography,
	Autocomplete,
	TextField,
	ListItemIcon,
	ClickAwayListener,
	Box,
	useTheme,
} from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useGetBrokerGroupsQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { getComparator, stableSort } from '@/components/table/functions';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

const CHIP_WIDTH = 120;
const RESERVED_SPACE = 48;

const ALL_OPTION = { id: 'all', name: 'All' }; // Option to select all groups in multi-select mode
const CLEAR_FILTER_OPTION = { id: 'clear', name: 'Clear Filter' }; // Option to clear all selections in multi-select mode

export const BrokerGroupSelect = ({
	brokerGroupId, // This is either a Firebase document id or an array of Firebase document ids - it is expected to be an array when multiple is true
	disabled,
	onChange,
	minimal,
	groupBy,
	multiple = false,
	isGroupDisabled = (group) => false, // Broker group that cannot be selected
	isGroupFixed = (group) => false, // Broker group that cannot be removed
	disableClearable = false,
	showAdmin = false,
	error = false,
	onBlur = (e) => {},
	showCheckbox = false,
	showAllOption: showAllOptionProp = false,
	showClearOption = false, // controls whether clear filter option appears
	availableGroups,
	...props
}) => {
	const { key, ...otherProps } = props;

	const theme = useTheme();
	const [open, setOpen] = useState(false);

	const containerRef = useRef();
	const [limitTags, setLimitTags] = useState(-1);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const userQuery = useGetUserDetailsQuery({ email }, { skip: !email });
	const brokerGroupIdsArray = userQuery.data?.brokerGroupArray ?? [];

	const { getFeatureFlags } = useFeatureFlagContext();
	const showAllOption =
		showAllOptionProp || Boolean(getFeatureFlags('renewalReportAllOption')?.active);

	const brokerGroupsQuery = useGetBrokerGroupsQuery(
		{ ids: brokerGroupIdsArray, showAdmin: showAdmin && userQuery.data?.isFolioAdmin },
		{
			skip: !brokerGroupIdsArray?.length,
		},
	);
	const availableBrokerGroups = availableGroups ?? brokerGroupsQuery.data ?? [];

	// Check if "All" is selected (for multi-select)
	const isAllSelected = useMemo(() => {
		if (!showAllOption) return false;

		if (multiple) {
			const selectableGroups = availableBrokerGroups.filter(
				(group) => !isGroupDisabled(group),
			);
			const selectedIds = (brokerGroupId ?? []).map((id) => String(id));
			return (
				selectableGroups.length > 0 &&
				selectableGroups.every((group) => selectedIds.includes(group.id))
			);
		} else {
			return brokerGroupId === 'all';
		}
	}, [multiple, availableBrokerGroups, brokerGroupId, isGroupDisabled, showAllOption]);

	const brokerGroups = (() => {
		const data = [...availableBrokerGroups].slice();
		const sorted = stableSort(data, getComparator('desc', groupBy ?? 'name'));

		const options = [];

		// Add "All" option for both single and multi-select
		if (showAllOption) {
			if (multiple) {
				// Multi-select: show "All" only if not all selected
				if (!isAllSelected) {
					options.push(ALL_OPTION);
				}
			} else {
				// Single-select: always show "All" if feature flag is on
				options.push(ALL_OPTION);
			}
		}

		// Add "Clear" option for multi-select only
		if (multiple && showClearOption && !disableClearable && brokerGroupId?.length > 0) {
			options.push(CLEAR_FILTER_OPTION);
		}

		return [...options, ...sorted];
	})();

	const selected = multiple
		? availableBrokerGroups.filter((item) =>
				(brokerGroupId ?? []).includes(item?.id),
			) || []
		: (() => {
				const groups = availableBrokerGroups;

				// If 'all' is selected, return the "All" option object
				if (brokerGroupId === 'all') {
					return ALL_OPTION;
				}

				return (
					groups.find(
						(item) =>
							brokerGroupId === item?.id ||
							(brokerGroupId &&
								Array.isArray(brokerGroupId) &&
								brokerGroupId.includes(item?.id)),
					) || null
				);
			})();

	useLayoutEffect(() => {
		if (!multiple || !containerRef.current) return;

		const containerWidth = containerRef.current.offsetWidth;

		const available = containerWidth - RESERVED_SPACE;
		const maxChips = Math.floor(available / CHIP_WIDTH);

		setLimitTags(maxChips > 0 ? maxChips : 1);
	}, [selected, multiple]);

	return (
		<>
			<ClickAwayListener onClickAway={(e) => setOpen(false)}>
				<Box sx={{ position: 'relative' }}>
					<Autocomplete
						ref={containerRef}
						open={open}
						onOpen={(e) => setOpen(true)}
						onClose={(e, reason) => {
							if (multiple && ['selectOption', 'removeOption'].includes(reason)) {
								return;
							}
							setOpen(false);
						}}
						fullWidth
						disabled={
							disabled ||
							(availableGroups == null &&
								(!brokerGroupsQuery.isSuccess || !userQuery.isSuccess))
						}
						isOptionEqualToValue={(option, val) => {
							if (option?.id === 'all' && val?.id === 'all') return true;
							if (option?.id === 'clear' && val?.id === 'clear') return true;
							return option?.id === val?.id;
						}}
						getOptionLabel={(option) => option?.name ?? ''}
						options={brokerGroups}
						loading={
							availableGroups == null &&
							(!brokerGroupsQuery.isSuccess || !userQuery.isSuccess)
						}
						value={selected}
						{...(groupBy && {
							groupBy: (option) => {
								if (option?.id === 'all') return '';
								if (option?.id === 'clear') return '';
								return option[groupBy];
							},
						})}
						onBlur={onBlur}
						multiple={multiple}
						disableClearable={disableClearable}
						onChange={(_, v) => {
							if (multiple) {
								// Filter out special options
								const selectedIds = (v ?? []).map((i) => i?.id);

								// Handle Clear Filter - but preserve fixed options
								if (selectedIds.includes('clear')) {
									// Clear all selections EXCEPT fixed options
									const fixedGroupIds = selected
										.filter((option) => isGroupFixed(option))
										.map((option) => option.id);
									onChange(fixedGroupIds);
									return;
								}

								// Handle All option
								if (selectedIds.includes('all')) {
									// Select all non-disabled groups
									const allGroupIds = availableBrokerGroups
										.filter((group) => !isGroupDisabled(group))
										.map((group) => group.id);
									onChange(allGroupIds);
									return;
								}

								// Regular multi-select behavior
								const fixedOptions = selected.filter((option) =>
									isGroupFixed(option),
								);
								const newValues = [...(v ?? []), ...fixedOptions]
									.filter(
										(option) => option.id !== 'all' && option.id !== 'clear',
									)
									.map((i) => i?.id)
									.reduce((acc, current) => {
										if (!acc.find((item) => item === current)) {
											acc.push(current);
										}
										return acc;
									}, []);
								onChange(newValues);
							} else {
								if (v?.id === 'clear') {
									const fixedGroup =
										selected?.id && isGroupFixed(selected) ? selected.id : null;
									onChange(fixedGroup);
									return;
								}

								if (v?.id === 'all') {
									onChange('all');
									return;
								}

								onChange(v?.id);
							}
						}}
						getOptionDisabled={(option) => {
							if (option?.id === 'all') return false;
							if (option?.id === 'clear') return false;
							return isGroupDisabled(option) || (multiple && isGroupFixed(option));
						}}
						renderOption={(props, option, { selected: isSelected }) => {
							const { key, ...optionProps } = props;
							const isChecked = isSelected || (option.id === 'all' && isAllSelected);
							const Icon = isChecked ? CheckBox : CheckBoxOutlineBlank;

							return (
								<li key={key} {...optionProps} style={{ width: '100%' }}>
									{showCheckbox && (
										<ListItemIcon sx={{ minWidth: '2.25em' }}>
											<Icon
												sx={{
													color: isChecked
														? theme.palette.primary.main
														: undefined,
												}}
											/>
										</ListItemIcon>
									)}
									<Typography>{option.name ?? ''}</Typography>
								</li>
							);
						}}
						limitTags={limitTags}
						renderInput={(params) => {
							return (
								<TextField
									{...params}
									onKeyDown={(e) => {
										e.preventDefault();
									}}
									error={error}
									onBlur={onBlur}
									slotProps={{
										input: {
											...params.InputProps,
											readOnly: true,

											style: {
												caretColor: 'transparent',
												cursor: 'pointer',
											},
											endAdornment: (
												<React.Fragment>
													{brokerGroupsQuery.isLoading ? (
														<CircularProgress
															color='inherit'
															size={20}
														/>
													) : null}
													{params.InputProps.endAdornment}
												</React.Fragment>
											),

											cursor: 'pointer',
										},
									}}
									sx={{
										cursor: 'pointer',
										...(multiple &&
											brokerGroupId?.length > 0 && {
												'& input': { display: 'none' },
											}),
									}}
								/>
							);
						}}
						{...(multiple && {
							renderValue: (value, getItemProps) =>
								value.map((option, index) => {
									const { key, ...itemProps } = getItemProps({ index });
									return (
										<Chip
											size='small'
											label={option.name ?? 'Unknown'}
											key={key}
											{...itemProps}
											disabled={multiple && isGroupFixed(option)}
										/>
									);
								}),
						})}
						key={key}
						{...(otherProps ?? {})}
						sx={{
							cursor: 'pointer',
							'& .MuiInputBase-root': {
								cursor: 'pointer',
								py: 0,
								flexWrap: 'nowrap', // prevents wrapping
								overflow: 'hidden',
							},
							'& .MuiAutocomplete-tag': {
								maxWidth: '100%',
							},
							'& input': {
								cursor: 'pointer',
							},
							...(props?.sx ?? {}),
						}}
					/>
				</Box>
			</ClickAwayListener>
		</>
	);
};
